import Pane from 'components/Common/Pane'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { memo, useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import TourV2DepartureTile from './TourV2DepartureTile'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_LONG_MONTH } from 'constants/dateFormats'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

const GroupTitle = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(12)} ${rem(20)};
`

function getGroupKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}`
}

interface Props {
  offer: App.Tours.TourV2Offer
  departures: Array<App.Tours.TourV2OfferDeparture>
  variation: App.Tours.TourV2OfferVariation
  onDepartureSelect?: (departure: App.Tours.TourV2OfferDeparture, keepBrowsing: boolean) => void
  onYearSelect?: (year: number) => void
  defaultDate?: Date
  travellers: Array<OfferPageRoom>
  packageType?: string
}

function TourV2DepartureTileList(props: Props) {
  const { departures, offer, onDepartureSelect, onYearSelect, defaultDate, travellers, variation, packageType } = props

  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const dateFormatter = useIntlDateFormatter()

  const departureGroups = useMemo(() => {
    const departuresByMonth = groupBy(departures, dep => {
      const date = new Date(dep.startDate)
      return getGroupKey(date)
    })

    const groups = departuresByMonth.entries().map(([key, departures]) => {
      const date = new Date(departures[0].startDate)
      return {
        key,
        date,
        month: dateFormatter(date, INTL_LONG_MONTH),
        year: date.getFullYear(),
        departures: sortBy(departures, dep => new Date(dep.startDate), 'asc'),
      }
    }).toArray()

    return sortBy(groups, group => group.date, 'asc')
  }, [dateFormatter, departures])

  const onYearClick = useCallback<React.MouseEventHandler<HTMLElement>>((e) => {
    const year = parseInt(e.currentTarget.dataset.year!)
    onYearSelect?.(year)
  }, [onYearSelect])

  useLayoutEffect(() => {
    if (defaultDate) {
      const element = groupRefs.current[getGroupKey(defaultDate)]
      if (element) {
        element.scrollIntoView({ behavior: 'instant', block: 'start' })
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <Pane background="neutral-six">
    {departureGroups.map((group) => <div
      key={group.key}
      ref={(ref: HTMLDivElement | null) => {
        groupRefs.current[group.key] = ref
      }}
    >
      <GroupTitle direction="horizontal" horizontalAlign="space-between" gap={20}>
        <Heading variant="heading5">{group.month}</Heading>
        <TextLink size="large" weight="bold" onClick={onYearClick} data-year={group.year}>
          {group.year}
        </TextLink>
      </GroupTitle>
      <VerticalSpacer gap={8} data-testid="tour-package-list">
        {group.departures?.map(departure => {
          return <TourV2DepartureTile
            key={departure.id}
            departure={departure}
            offer={offer}
            onSelect={onDepartureSelect}
            travellers={travellers}
            variation={variation}
            packageType={packageType}
          />
        })}
      </VerticalSpacer>
    </div>)}
  </Pane>
}

export default memo(TourV2DepartureTileList)
